import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, FolderOpen } from "lucide-react";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import { HoldingWithInstrumentAndAccount } from "@/types";
import { HoldingComparison } from "../hooks/useHoldingsComparisonController";
import { PeriodSelector } from "./PeriodSelector";
import { ComparisonTable } from "./ComparisonTable";
import { ComparisonSummary } from "./ComparisonSummary";
import { SearchBar } from "./SearchBar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { useAtomValue } from "jotai";

interface HoldingsComparisonViewProps {
  primaryPeriod: ReportingPeriod | null;
  secondaryPeriod: ReportingPeriod | null;
  primaryHoldings: HoldingWithInstrumentAndAccount[];
  secondaryHoldings: HoldingWithInstrumentAndAccount[];
  comparisonData: HoldingComparison[];
  isLoading: boolean;
  onSelectPrimaryPeriod: (period: ReportingPeriod) => void;
  onSelectSecondaryPeriod: (period: ReportingPeriod) => void;
  onClearSecondaryPeriod?: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

// Helper function to format currency
const formatCurrency = (amount: number, currency: string = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

// Helper function to filter holdings based on search query
const filterHoldings = (holdings: HoldingWithInstrumentAndAccount[], searchQuery: string) => {
  if (!searchQuery.trim()) return holdings;
  
  const search = searchQuery.toLowerCase();
  return holdings.filter(holding =>
    holding.instrument.name.toLowerCase().includes(search) ||
    holding.instrument.instrumentKey.toLowerCase().includes(search)
  );
};

export const HoldingsComparisonView: React.FC<HoldingsComparisonViewProps> = ({
  primaryPeriod,
  secondaryPeriod,
  primaryHoldings,
  secondaryHoldings,
  comparisonData,
  isLoading,
  onSelectPrimaryPeriod,
  onSelectSecondaryPeriod,
  onClearSecondaryPeriod,
  searchQuery,
  setSearchQuery,
}) => {
  const filteredPrimaryHoldings = filterHoldings(primaryHoldings, searchQuery);
  const selectedFund = useAtomValue(SelectedFundAtom);
  if(!selectedFund) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center min-h-[400px]">
        <div className="bg-primary/10 p-6 rounded-full mb-6">
          <FolderOpen className="h-12 w-12 text-primary" />
        </div>
        <h2 className="text-2xl font-semibold mb-2">No Fund Selected</h2>
        <p className="text-muted-foreground mb-6 max-w-md">
          Please select a fund from the sidebar to view and compare holdings across different reporting periods.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-8xl space-y-6">
      {/* Period Selection */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Primary Period</CardTitle>
            <CardDescription>Current reporting period for comparison baseline</CardDescription>
          </CardHeader>
          <CardContent>
            <PeriodSelector
              selectedPeriod={primaryPeriod}
              onSelectPeriod={onSelectPrimaryPeriod}
              label="Select Primary Period"
            />
            {primaryPeriod && (
              <div className="mt-4 text-sm text-muted-foreground">
                Holdings: {primaryHoldings.length}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Secondary Period (Optional)</CardTitle>
            <CardDescription>Compare against this reporting period</CardDescription>
          </CardHeader>
          <CardContent>
            <PeriodSelector
              selectedPeriod={secondaryPeriod}
              onSelectPeriod={onSelectSecondaryPeriod}
              onClearSelection={onClearSecondaryPeriod}
              label="Select Secondary Period"
              allowEmpty={true}
            />
            {secondaryPeriod && (
              <div className="mt-4 text-sm text-muted-foreground">
                Holdings: {secondaryHoldings.length}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Comparison Summary */}
      {secondaryPeriod && (
        <ComparisonSummary comparisonData={comparisonData} />
      )}

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Holdings {secondaryPeriod ? 'Comparison' : 'Details'}</CardTitle>
          <CardDescription>
            {secondaryPeriod && primaryPeriod
              ? `Comparing holdings between ${new Date(primaryPeriod.period).toLocaleDateString()} and ${new Date(secondaryPeriod.period).toLocaleDateString()}`
              : primaryPeriod 
                ? `Holdings for ${new Date(primaryPeriod.period).toLocaleDateString()}`
                : 'Select a reporting period to view holdings'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <SearchBar 
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              placeholder="Search holdings by instrument name or key..."
            />
          </div>

          {secondaryPeriod ? (
            <ComparisonTable 
              comparisonData={comparisonData}
              isLoading={isLoading}
            />
          ) : primaryPeriod ? (
            <div>
              <div className="mb-4 text-sm text-muted-foreground">
                Showing {filteredPrimaryHoldings.length} of {primaryHoldings.length} holdings for {new Date(primaryPeriod.period).toLocaleDateString()}
              </div>
              
              {isLoading ? (
                <div className="text-center py-8">Loading holdings...</div>
              ) : filteredPrimaryHoldings.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Instrument</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Market Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPrimaryHoldings.map((holding) => (
                        <TableRow key={holding.holdingId}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{holding.instrument.name}</div>
                              <div className="text-sm text-muted-foreground">{holding.instrument.instrumentKey}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {holding.quantity.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {holding.quantity > 0 ? formatCurrency(holding.baseAmount / holding.quantity, holding.currency) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(holding.baseAmount, holding.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery ? 'No holdings match your search criteria' : 'No holdings found for this period'}
                </div>
              )}
              
              <div className="mt-4 text-center text-muted-foreground">
                <p>Select a secondary period to compare holdings side by side</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>Select a primary reporting period to view holdings</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 